import React, { useEffect, useState } from "react";
import { Card, Table, Tag, Statistic, Row, Col, Tabs, App } from "antd";
import type { ColumnsType } from "antd/es/table";
import { WarningOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import type { ILowStockItem } from "../../interfaces/stock";
import { stockService } from "../../api/stockService";
import { PageHeader } from "../../components/ui";

export const StockAlerts: React.FC = () => {
    const { message } = App.useApp();
    const [lowStock, setLowStock] = useState<ILowStockItem[]>([]);
    const [outOfStock, setOutOfStock] = useState<ILowStockItem[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        void fetchAlerts();
    }, []);

    const fetchAlerts = async () => {
        try {
            setLoading(true);
            const [lowStockData, outOfStockData] = await Promise.all([
                stockService.getLowStockItems(),
                stockService.getOutOfStockItems(),
            ]);
            setLowStock(Array.isArray(lowStockData) ? lowStockData : []);
            setOutOfStock(Array.isArray(outOfStockData) ? outOfStockData : []);
        } catch {
            message.error("Could not load stock alerts.");
        } finally {
            setLoading(false);
        }
    };

    const columns: ColumnsType<ILowStockItem> = [
        {
            title: "Product Code",
            dataIndex: "productCode",
            key: "productCode",
            render: (text: string, record: ILowStockItem) => (
                <Link to={`/products/show/${record.inventoryItemId}`}>{text}</Link>
            ),
        },
        {
            title: "Product Name",
            dataIndex: "productName",
            key: "productName",
        },
        {
            title: "Current Stock",
            dataIndex: "currentStock",
            key: "currentStock",
            render: (value: number) => (
                <span style={{ fontWeight: "bold", color: value === 0 ? "#ff4d4f" : "#faad14" }}>
                    {value.toFixed(2)}
                </span>
            ),
        },
        {
            title: "Reorder Point",
            dataIndex: "reorderPoint",
            key: "reorderPoint",
            render: (value: number) => value.toFixed(2),
        },
        {
            title: "Recommended Order",
            dataIndex: "recommendedOrderQuantity",
            key: "recommendedOrderQuantity",
            render: (value: number) => <Tag color="blue">{value.toFixed(2)}</Tag>,
        },
        {
            title: "Alert Level",
            dataIndex: "alertLevel",
            key: "alertLevel",
            render: (value: string) => {
                let color = "default";
                if (value === "Critical" || value === "Out of Stock") color = "error";
                else if (value === "Low") color = "warning";
                return <Tag color={color}>{value}</Tag>;
            },
        },
        {
            title: "",
            key: "actions",
            width: 100,
            render: () => <Link to="/stock">Adjust stock</Link>,
        },
    ];

    return (
        <div className="page-container">
            <PageHeader
                title="Stock alerts"
                titleIcon={<WarningOutlined />}
                breadcrumbs={[
                    { title: "Stock", href: "/stock" },
                    { title: "Alerts" },
                ]}
                actions={[
                    { label: "Stock levels", href: "/stock", type: "default", variant: "outlined" },
                    { label: "Inventory report", href: "/reports?tab=inventory", type: "default", variant: "outlined" },
                ]}
            />

            <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col span={12}>
                    <Card>
                        <Statistic
                            title="Low stock items"
                            value={lowStock.length}
                            prefix={<WarningOutlined />}
                            valueStyle={{ color: "#faad14" }}
                        />
                    </Card>
                </Col>
                <Col span={12}>
                    <Card>
                        <Statistic
                            title="Out of stock items"
                            value={outOfStock.length}
                            prefix={<CloseCircleOutlined />}
                            valueStyle={{ color: "#ff4d4f" }}
                        />
                    </Card>
                </Col>
            </Row>

            <Card>
                <Tabs
                    items={[
                        {
                            key: "low",
                            label: `Low stock (${lowStock.length})`,
                            children: (
                                <Table
                                    columns={columns}
                                    dataSource={lowStock}
                                    rowKey="inventoryItemId"
                                    loading={loading}
                                    pagination={{ pageSize: 20 }}
                                />
                            ),
                        },
                        {
                            key: "out",
                            label: `Out of stock (${outOfStock.length})`,
                            children: (
                                <Table
                                    columns={columns}
                                    dataSource={outOfStock}
                                    rowKey="inventoryItemId"
                                    loading={loading}
                                    pagination={{ pageSize: 20 }}
                                />
                            ),
                        },
                    ]}
                />
            </Card>
        </div>
    );
};

export default StockAlerts;
